import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { apiCall } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { SUBSCRIPTION_UNRESTRICTED_ENTITY_TYPES } from '../../core/entityTypes';
import {
  getStoredObject,
  getStoreGeneration,
  hasCompleteObject,
  registerEntityAlias,
  resolveEntityId,
  upsertObject,
  upsertSummaryObjects,
} from '../../store/crmObjectStore';
import {
  usePendingCreation404Recovery,
  useSelfHealRefetch,
} from '../../store/hooks';

export interface RecordSlideoutQueryResult {
  loading: boolean;
  error: Error | null;
  isFetching: boolean;
  querySucceeded: boolean;
  refetch: () => void;
  statusCode: number | undefined;
  isPendingCreation: boolean;
  suppress404: boolean;
}

export interface RecordSlideoutQueryInput {
  objectId: string;
  orgId: number | undefined;
  hasCreateOverlay: boolean;
  storeObjectIsComplete: boolean;
}

/**
 * Encapsulates the CRM object fetch for the record slideout/panel:
 * - skip-gate (store-complete + successful query + subscription-unrestricted)
 * - queryFn with store-generation race guard and associated_objects upsert
 * - self-heal refetch when the store entry goes missing after a successful fetch
 * - pending-creation 404 recovery
 */
export function useRecordSlideoutQuery({
  objectId,
  orgId,
  hasCreateOverlay,
  storeObjectIsComplete,
}: RecordSlideoutQueryInput): RecordSlideoutQueryResult {
  const queryClient = useQueryClient();

  // Skip the fetch when the store already has a complete entry AND a
  // successful slideout query exists for this entity (meaning the overlay has
  // been fetched and upserted into the store) AND the entity type is
  // subscription-unrestricted (restricted types receive no live WS store
  // updates without a subscription, so their fetch-on-open is the only
  // freshness path — skipping only for PROVEN-unrestricted types is
  // fail-safe, see SUBSCRIPTION_UNRESTRICTED_ENTITY_TYPES).
  //
  // The skip-gate keys on request STATUS rather than cached body shape.
  // The overlay lives in the store (upserted by queryFn alongside the main
  // object), so the relevant fact is whether a successful slideout fetch has
  // completed — not whether the query cache still holds any particular value.
  // A successful status entry persists until invalidateSlideoutCache removes
  // it; removal re-enables the fetch so the overlay refreshes.
  //
  // `getStoredObject` is a synchronous snapshot; re-renders triggered by the
  // useSyncExternalStore subscription in useStoreObjectSubscription keep this
  // gate reactive.
  //
  // Resolve through the merge-alias map before every store/cache lookup.
  // `hasCompleteObject` already resolves the alias internally, but the query
  // cache keys by the exact id — so for a merged-record open (a pre-merge id
  // aliased to a canonical entry) the raw lookup misses the canonical entry.
  // Apply the same getQueryState check to the resolved id when it differs so
  // alias opens see a prior direct-open status entry, matching hasCompleteObject.
  const resolvedObjectId = resolveEntityId(objectId);
  const hasSuccessfulQuery =
    queryClient.getQueryState(['crm', orgId, 'object', objectId])?.status ===
      'success' ||
    (resolvedObjectId !== objectId &&
      queryClient.getQueryState(['crm', orgId, 'object', resolvedObjectId])
        ?.status === 'success');
  const typeForGate =
    getStoredObject(resolvedObjectId)?.data.entity_type_name ?? '';
  const skipFetch =
    hasCompleteObject(objectId) &&
    hasSuccessfulQuery &&
    Boolean(typeForGate) &&
    SUBSCRIPTION_UNRESTRICTED_ENTITY_TYPES.has(typeForGate);

  // Data fetching — queryFn returns a null sentinel: the overlay lives in the
  // store (upserted alongside the main object so associated_objects is
  // preserved across WS updates), and the skip-gate keys on request STATUS
  // rather than cached body shape. Components read associated_objects from
  // storeObjectData.
  const {
    isLoading: loading,
    error,
    isFetching,
    isSuccess: querySucceeded,
    refetch,
  } = useQuery<null, Error, null>({
    queryKey: ['crm', orgId, 'object', objectId],
    queryFn: async () => {
      if (!orgId) {
        throw new Error('Organization required');
      }
      // Snapshot the store generation before the fetch: if an org switch (or
      // any other eviction boundary) clears the store while this request is
      // in flight, the generation changes and the writes below must be
      // skipped — otherwise a fetch started before the switch could write
      // stale org-A data into the now-cleared org-B store. Mirrors the
      // snapshot/check idiom in entityFetchBatcher.ts's refreshStoreEntities.
      const generationAtStart = getStoreGeneration();
      const data = await apiCall<
        CrmObject & { associated_entities?: Record<string, CrmObject> }
      >(`/api/orgs/${orgId}/crm-events/entities/${objectId}/`);

      if (getStoreGeneration() !== generationAtStart) {
        logger.debug(
          '[RecordSurfaceProvider] store generation changed during fetch — discarding stale write',
          { objectId, orgId }
        );
        return null;
      }

      const associatedEntities = data.associated_entities;
      if (associatedEntities && Object.keys(associatedEntities).length > 0) {
        upsertSummaryObjects(Object.values(associatedEntities));
      }

      const { associated_entities: _, ...mainObject } = data;
      // A merged record resolves server-side to its canonical survivor (a
      // different entity_id). The store keys by the canonical id, so record
      // the alias — exactly as the batch fetchers do — or the subscription on
      // the requested id never sees the entry and the panel reports the
      // record as missing (and the self-heal refetch loops on it).
      if (mainObject.entity_id !== objectId) {
        registerEntityAlias(objectId, mainObject.entity_id);
      }
      // Upsert WITH the overlay so the store carries associated_objects for
      // downstream consumers (e.g. EmailThreadOverviewEnrichment) without a
      // separate roundtrip. The query returns null — the overlay is read from
      // the store, not from the React Query cache.
      upsertObject({ ...mainObject, associated_objects: associatedEntities });
      return null;
    },
    enabled: Boolean(orgId) && !skipFetch && !hasCreateOverlay,
  });

  // Self-heal when the store entry is missing/incomplete after a successful
  // fetch (e.g. an eviction boundary — org switch, list type navigation —
  // raced the fetch and cleared the store entry the queryFn just wrote).
  // Guarded on the STABLE objectId input so this can't loop.
  const isObjectMissingForHeal = useCallback(
    () => !hasCompleteObject(objectId),
    // storeObjectIsComplete is not read inside this predicate — hasCompleteObject
    // queries the store fresh on each call. It is listed as a dep so this
    // callback's identity changes (triggering the helper's effect) whenever
    // the store's completeness flag flips for this objectId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objectId, storeObjectIsComplete]
  );
  useSelfHealRefetch({
    enabled: Boolean(orgId) && !hasCreateOverlay,
    guardKey: objectId,
    isObjectMissing: isObjectMissingForHeal,
    querySucceeded,
    queryIsFetching: isFetching,
    refetch,
    label:
      '[RecordSurfaceProvider] store entry missing after success — refetching',
    logContext: { objectId, orgId },
  });

  const statusCode = (error as { statusCode?: number } | null)?.statusCode;

  // A 404 is only authoritative when the record is NOT a pending optimistic
  // creation. While pending, the server simply hasn't projected the entity
  // yet — the store copy the user just created is the truth, and rendering
  // "Record not found" would misrepresent an in-flight write as a missing
  // record. Once pending resolves (confirmed, or the orphan timer expired),
  // the hook refetches: success renders the record; another 404 is now
  // authoritative and falls through to the not-found state below.
  const isPendingCreation = usePendingCreation404Recovery({
    entityId: objectId,
    statusCode,
    refetch,
  });
  const suppress404 = statusCode === 404 && isPendingCreation;

  return useMemo(
    () => ({
      loading,
      error,
      isFetching,
      querySucceeded,
      refetch,
      statusCode,
      isPendingCreation,
      suppress404,
    }),
    [
      loading,
      error,
      isFetching,
      querySucceeded,
      refetch,
      statusCode,
      isPendingCreation,
      suppress404,
    ]
  );
}
